import { intl } from '@fullstory/intl';
import {
  BuiltInDimension,
  Dimension,
  Measure,
  UniqueAggregation,
} from '@fullstory/proto/fs/api/metrics';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { TimeInterval } from '@fullstory/proto/fs/api/segment';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { Display, DisplayPrecision } from '@fullstory/proto/fs/services/webber/frontend/display';
import { CustomField } from '@fullstory/search-vars';

export const DEFAULT_DISPLAY: Display = {
  number: true,
  displayPrecision: DisplayPrecision.PRECISION_0,
};

export const DEFAULT_MEASURE: Measure = {
  event: {
    primary: null,
    deps: [],
  },
  aggregation: {
    unique: UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL,
  },
  rollingWindow: 0,
  rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
  rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
};

export const PERCENT_UNMODIFIED_DISPLAY: Display = {
  displayPrecision: DisplayPrecision.PRECISION_0,
  percentUnmodified: true,
};

export const DEFAULT_DUAL_DIVIDE_DISPLAY: Display = {
  percent: true,
  displayPrecision: 1,
};

export const DEFAULT_METRIC_NAME = intl.formatMessage({
  defaultMessage: 'Any Activity',
  id: 'WnKl2b',
});

export enum UTM_PREFIXES {
  UTM_SOURCE = 'utm_source=',
  UTM_MEDIUM = 'utm_medium=',
  UTM_CAMPAIGN = 'utm_campaign=',
}

export const ERROR_CLICK_TYPE = {
  unknown: intl.formatMessage({
    defaultMessage: 'Unknown',
    id: 'O0SLRP',
    description: 'Unknown error click type',
  }),
  console: intl.formatMessage({ defaultMessage: 'Console Error', id: 'bHkWhE' }),
  exception: intl.formatMessage({ defaultMessage: 'Uncaught Exception', id: 'iXdhdC' }),
};

export interface LabelGroup {
  groups: string[];
  label: string;
  id: string;
  pillLabel?: DimensionPillLabel;
  fallback?: boolean;
}

export enum DimensionPillCategory {
  invalid = '',
  namedElement = 'Named CSS Element',
  pageDef = 'Page Definition',
}

export interface DimensionPillLabel {
  value: string;
  category: DimensionPillCategory;
  namedElement?: NamedElementRecord;
  pageDef?: PageDefinition;
}

export type DimensionNoConfig = Omit<Dimension, 'limit' | 'computeAtLevel' | 'eventRefinement'>;

export interface DimensionConfig {
  noFallback: boolean;
}

export interface DimensionState {
  dimensions: DimensionNoConfig[];
  noFallback?: boolean;
  label: string;
  delimiter?: string;
}

export interface DimensionSpecField {
  parentDisplayName: string;
}

export interface DimensionalityPropertyOption extends Omit<DimensionState, 'noFallback'> {
  type: Dimension.property_keys;
  field?: CustomField;
  spec?: DimensionSpecField;
  id: string;
}

export const cityDimension = {
  dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_CITY, prefix: '' }],
  label: intl.formatMessage({ defaultMessage: 'City', id: 'TE4fIS' }),
};

export const cityStateDimension = {
  dimensions: [
    { builtInProperty: BuiltInDimension.DIMENSION_CITY, prefix: '' },
    { builtInProperty: BuiltInDimension.DIMENSION_STATE, prefix: '' },
  ],
  label: intl.formatMessage({ defaultMessage: 'City', id: 'TE4fIS' }),
};

export const contextualDimensions = {
  apiEvent: [
    {
      dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_API_EVENT_NAME, prefix: '' }],
      label: intl.formatMessage({ defaultMessage: 'API Event', id: 'XOChST' }),
    },
  ],
  errorType: [
    {
      dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_ERROR_CLICK_TYPE, prefix: '' }],
      label: intl.formatMessage({ defaultMessage: 'Error Type', id: 'tvcc83' }),
    },
  ],
  networkError: [
    {
      dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_AJAX_REQUEST_METHOD, prefix: '' }],
      label: intl.formatMessage({ defaultMessage: 'Request Method', id: '0DvwSl' }),
    },
    {
      dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_AJAX_REQUEST_STATUS, prefix: '' }],
      label: intl.formatMessage({ defaultMessage: 'Request Status', id: 'V7Ap/9' }),
    },
    {
      dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_AJAX_REQUEST_URL, prefix: '' }],
      label: intl.formatMessage({ defaultMessage: 'Request URL', id: 'fw4uzU' }),
    },
    {
      dimensions: [
        { builtInProperty: BuiltInDimension.DIMENSION_AJAX_REQUEST_URL_HOST, prefix: '' },
      ],
      label: intl.formatMessage({ defaultMessage: 'Request URL Host', id: 'TMi//0' }),
    },
    {
      dimensions: [
        { builtInProperty: BuiltInDimension.DIMENSION_AJAX_REQUEST_URL_FRAGMENT, prefix: '' },
      ],
      label: intl.formatMessage({ defaultMessage: 'Request URL Fragment', id: 'dXk5Z/' }),
    },
    {
      dimensions: [
        { builtInProperty: BuiltInDimension.DIMENSION_AJAX_REQUEST_URL_QUERY, prefix: '' },
      ],
      label: intl.formatMessage({ defaultMessage: 'Request URL Query', id: 'GqqW1M' }),
    },
    {
      dimensions: [
        { builtInProperty: BuiltInDimension.DIMENSION_AJAX_REQUEST_URL_PATH, prefix: '' },
      ],
      label: intl.formatMessage({ defaultMessage: 'Request URL Path', id: 'J3P4EG' }),
    },
  ],
  uncaughtException: [
    {
      dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL, prefix: '' }],
      label: intl.formatMessage({ defaultMessage: 'Web Source File URL', id: 'KxjtJ9' }),
    },
    {
      dimensions: [
        { builtInProperty: BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_HOST, prefix: '' },
      ],
      label: intl.formatMessage({ defaultMessage: 'Web Source File URL Host', id: 'DJhjLw' }),
    },
    {
      dimensions: [
        { builtInProperty: BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_FRAGMENT, prefix: '' },
      ],
      label: intl.formatMessage({ defaultMessage: 'Web Source File URL Fragment', id: 'Jw+OHv' }),
    },
    {
      dimensions: [
        { builtInProperty: BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_QUERY, prefix: '' },
      ],
      label: intl.formatMessage({ defaultMessage: 'Web Source File URL Query', id: 'BLvQGX' }),
    },
    {
      dimensions: [
        { builtInProperty: BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_PATH, prefix: '' },
      ],
      label: intl.formatMessage({ defaultMessage: 'Web Source File URL Path', id: '1yNH2g' }),
    },
  ],
};

export const builtInWithoutContextual: DimensionState[] = [
  cityDimension,
  cityStateDimension,
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_CSS, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Element / CSS Selector', id: '/biIHN' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_STATE, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'State', id: 'ku+mDU' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_COUNTRY, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Country', id: 'vONi+O' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_IP_ADDRESS, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'IP Address', id: 'uJV0wU' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_EMAIL, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Email', id: 'sy+pv5' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_USER_ID, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'User ID', id: '55vTH+' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_NAME, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Name', id: 'HAlOn1' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_USER_AGENT, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'User Agent', id: 'rO9P0k' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_APP_VIEW_NAME, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Screen', id: 'kzaYlD' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_APP_PACKAGE_NAME, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'App Package Name', id: 'VW156C' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_APP_NAME, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'App Name', id: 'Npu1CM' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_DEVICE_MODEL, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Device Model', id: 'e/WYt2' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_DEVICE_VENDOR, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Device Vendor', id: 'Ly96yO' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_BROWSER, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Browser', id: 'E3oB+t' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_BROWSER_VERSION, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Browser Version', id: 'i9WP0T' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_DEVICE, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Device', id: 'c3VJQo' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_PLATFORM, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Platform', id: 'Mmuj1R' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_OS, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Operating System', id: 't2uvIi' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_APP_VERSION, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'App Version', id: 'OwQmnm' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_APP_OS_VERSION, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'App OS Version', id: 'EeONtU' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_APP_FS_VERSION, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'App FullStory SDK Version', id: 'wDXRIz' }),
  },
  { dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_URL, prefix: '' }], label: 'URL' },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_URL_HOST, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'URL Host', id: '06KAly' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_URL_FRAGMENT, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'URL Fragment', id: 'G95Lq4' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_URL_QUERY, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'URL Query', id: 'NFZXsa' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_URL_HASH_QUERY, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'URL Hash Query', id: 'Wcy+t7' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_URL_PATH, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'URL Path', id: '+WBVrU' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_URL_HASH_PATH, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'URL Hash Path', id: 'cnSmyE' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_REFERRER_URL, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Referrer URL', id: 'oebwED' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_REFERRER_HOST, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Referrer Host', id: 'hBOL4a' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_REFERRER_FRAGMENT, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Referrer Fragment', id: 'qvHQmE' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_REFERRER_QUERY, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Referrer Query', id: 'an2q4S' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_REFERRER_PATH, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Referrer Path', id: 'sCVwkx' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_TEXT, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Text', id: 'aA8bDw' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_MOBILE_SOURCE_FILE, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Mobile Source File', id: '1DBBYB' }),
  },
  {
    dimensions: [{ builtInProperty: BuiltInDimension.DIMENSION_PAGE_DEF_ID, prefix: '' }],
    label: intl.formatMessage({ defaultMessage: 'Page', id: 'wFmAFC' }),
  },
  {
    dimensions: [
      { builtInProperty: BuiltInDimension.DIMENSION_SCREEN_WIDTH, prefix: '' },
      { builtInProperty: BuiltInDimension.DIMENSION_SCREEN_HEIGHT, prefix: '' },
    ],
    label: intl.formatMessage({ defaultMessage: 'Screen Resolution', id: '0jGmLC' }),
    delimiter: ' x ',
  },
  // Canned UTM dimensions, same builtInProperty as URL Query but different prefixes and labels
  {
    dimensions: [
      {
        builtInProperty: BuiltInDimension.DIMENSION_URL_QUERY,
        prefix: UTM_PREFIXES.UTM_SOURCE,
      },
    ],
    label: intl.formatMessage({ defaultMessage: 'UTM Source', id: 'QgMK2h' }),
  },
  {
    dimensions: [
      {
        builtInProperty: BuiltInDimension.DIMENSION_URL_QUERY,
        prefix: UTM_PREFIXES.UTM_CAMPAIGN,
      },
    ],
    label: intl.formatMessage({ defaultMessage: 'UTM Campaign', id: '+SwUa5' }),
  },
  {
    dimensions: [
      {
        builtInProperty: BuiltInDimension.DIMENSION_URL_QUERY,
        prefix: UTM_PREFIXES.UTM_MEDIUM,
      },
    ],
    label: intl.formatMessage({ defaultMessage: 'UTM Medium', id: 'kT8vGm' }),
  },
];

export const builtInDimensions = builtInWithoutContextual.concat(
  ...Object.values(contextualDimensions),
);

export const NATIVE_MOBILE_ONLY = [
  BuiltInDimension.DIMENSION_APP_VIEW_NAME,
  BuiltInDimension.DIMENSION_APP_PACKAGE_NAME,
  BuiltInDimension.DIMENSION_APP_NAME,
  BuiltInDimension.DIMENSION_APP_VERSION,
  BuiltInDimension.DIMENSION_DEVICE_MODEL,
  BuiltInDimension.DIMENSION_DEVICE_VENDOR,
  BuiltInDimension.DIMENSION_PLATFORM,
  BuiltInDimension.DIMENSION_APP_OS_VERSION,
  BuiltInDimension.DIMENSION_APP_FS_VERSION,
];

export const WEB_EXCEPTION_ONLY = [
  BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL,
  BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_HOST,
  BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_FRAGMENT,
  BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_QUERY,
];

export const MOBILE_EXCEPTION_ONLY = [BuiltInDimension.DIMENSION_MOBILE_SOURCE_FILE];
